import { useCallback, useMemo } from 'react'

import type { ApplicantStage } from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_PIPELINE_ACTIVITY } from 'src/graphql/pipelineMetrics.graphql'
import {
  TrendChartView,
  type TrendBucket,
} from 'src/pages/AnalyticsPage/components/TrendChart'
import {
  buildTrendSeriesFromPalette,
  useAutoBucket,
  useRefetchOnKeyChange,
} from 'src/pages/AnalyticsPage/components/trendChartHelpers'

import { STAGE_ORDER } from './stagePalette'

const INFO_TEXT =
  'Applicants by stage over time, bucketed by day, week, or month. Each line uses the stage’s own timestamp column on Applicant — Hired uses hiredDate, Disqualified uses disqualifiedDate, etc. The bucket adapts to your selected date range; you can override it with the toggle.'

interface PipelineActivityPointShape {
  periodStart: string
  count: number
}
interface PipelineActivityStageSeriesShape {
  stage: ApplicantStage
  points: ReadonlyArray<PipelineActivityPointShape>
}
interface PipelineActivityShape {
  bucket: TrendBucket
  series: ReadonlyArray<PipelineActivityStageSeriesShape>
}

export interface PipelineActivityContainerProps {
  /** Page-level FiltersBar state — the resolver honors every facet. */
  filters: {
    dateRange: { gte: Date; lte: Date }
    recruiters?: string[]
    jobListings?: string[]
    linkTypes?: string[]
    jobTypes?: string[]
  }
  /**
   * Bumped by the page-level Refresh button to trigger a manual refetch.
   * Mount value is 0 — the initial useQuery already fetches, so the first
   * change to this prop (i.e. ≥ 1) is what drives a refetch.
   */
  refreshKey: number
}

const PipelineActivityContainer = ({
  filters,
  refreshKey,
}: PipelineActivityContainerProps) => {
  const { dateRange } = filters
  const [bucket, setBucket] = useAutoBucket(
    dateRange.gte.getTime(),
    dateRange.lte.getTime()
  )

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  )

  const { data, loading, refetch } = useQuery(GET_PIPELINE_ACTIVITY, {
    variables: { input: { filters, bucket, timezone } },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  useRefetchOnKeyChange(refetch, refreshKey)

  const activity = data?.pipelineActivity as PipelineActivityShape | undefined

  const trendData = useMemo(() => {
    if (!activity) return undefined
    const series = buildTrendSeriesFromPalette(
      // STAGE_ORDER drives display order (Prospects → Hired → … → Terminated)
      // so the chart's legend matches the Summary card's row order.
      STAGE_ORDER.map((s) => ({
        id: s.stage,
        label: s.label,
        color: s.color,
      })),
      activity.series,
      (s) => s.stage
    )
    return { bucket: activity.bucket, series }
  }, [activity])

  return (
    <TrendChartView
      title="Pipeline Activity"
      info={INFO_TEXT}
      data={trendData}
      loading={loading}
      bucket={bucket}
      onBucketChange={setBucket}
      tooltipUnit="applicant"
      emptyMessage="No applicants created in this period"
    />
  )
}

export default PipelineActivityContainer
